import { createPortal } from "react-dom";
import { SelectPrettyValue } from "../../market-grid/components/SelectPretty";
import { Radio, RadioGroup } from "@artis/ui";
import { Dropdown } from "../../ui/dropdown";
import { useCallback, useEffect, useRef, useState, useMemo, type RefObject } from "react";
import { Button } from "../../ui/button";
import { LuInfo } from "react-icons/lu";
import * as R from "remeda";
import cn from "mcn";
import { CloseIcon } from "../../icons/CloseIcon";
import { FaChevronDown } from "react-icons/fa";
import ClickAwayListener from "../ClickAwayListener";

import { cardContainer } from "../../ui/shared-styles";
import { productName } from "../../utils";
import { changeAllZIndex } from "../../globals";
import {
  knownStatusList,
  joinProductsWithGridPageProducts,
  type TAffectedProducts,
  type TProductWithGridIdAndStatus,
  type TKnownStatus,
  type TStatus,
} from "../../market-grid/statuses/statusLogic";
import {
  knownStatusRules,
  statusModalMaxWidth,
  StatusOperationSchema,
  type TStatusTransitionEnhanced,
  type TUpdateStatusOperation,
} from "../../market-grid/statuses/consts";

import {
  useUpdateColumnStatus,
  useWriteStatusInstruments,
  useGlobalDependencyOptions,
} from "../../market-grid/statuses/hooks";
import {
  checkCanBroadcast,
  genSelectStatusOptions,
  genStatusTransitionActions,
  verifyKnownStatusRules,
} from "../../market-grid/statuses/utils";
import {
  type TGlobalPackagesByProductIds,
  type TGlobalProductsByProductIds,
  useOptionalActivePageId,
  useGlobalPackagesByProductIds,
  useGlobalProductsByProductIds,
  useGridSettings,
  useGridSettingsStatusMap,
  useProductsByIds,
} from "../../../data";
import { logger } from "@artis/logger";

import { useAllPageProducts } from "../../../data/pages";
import { useUserHasRole, useUserSubscriptionTier } from "../../../data/user";
import { useUserId } from "../../../context/auth/hooks";

function getGlobalProductsBySource(
  source: number | null | undefined,
  globalProducts: TGlobalProductsByProductIds,
  globalDependenciesDetails: TGlobalPackagesByProductIds,
) {
  if (!source || !globalProducts || !globalDependenciesDetails) {
    return [];
  }

  const globalProductsBySource = globalProducts.reduce<Record<number, Array<string>>>((acc, product) => {
    const globalPackageId = product.global_package;
    const details = globalDependenciesDetails.find((pkg) => pkg.id === globalPackageId);

    if (details?.source) {
      acc[details.source] = acc[details.source] || [];
      acc[details.source].push(product.id);
    }

    return acc;
  }, {});

  return globalProductsBySource[source] || [];
}

async function handleUpdateStatus({
  statusOperations,
  isDeniedBroadcast,
  isLiteUser,
  toStatus,
  affectedGridProducts,
  transitionActions,
  updateColumnStatus,
  writeStatusInstruments,
  statusMap,
  globalProducts,
  globalDependenciesDetails,
}: {
  statusOperations: Record<string, TUpdateStatusOperation>;
  isDeniedBroadcast: boolean;
  isLiteUser: boolean;
  toStatus: TStatus;
  affectedGridProducts: TProductWithGridIdAndStatus[] | undefined;
  transitionActions: TStatusTransitionEnhanced[];
  updateColumnStatus: ReturnType<typeof useUpdateColumnStatus>;
  writeStatusInstruments: ReturnType<typeof useWriteStatusInstruments>;
  statusMap: Record<string, TStatus>;
  globalProducts: TGlobalProductsByProductIds;
  globalDependenciesDetails: TGlobalPackagesByProductIds;
}) {
  const toStatusIsGlobalProduct = !knownStatusList.includes(toStatus);
  const toStatusMap: Record<string, TStatus> = {};

  const productDetail = globalProducts?.find((prod) => prod.id === toStatus);
  const globalPackageId = productDetail?.global_package;
  const globalPackageDetail = globalDependenciesDetails?.find((pkg) => pkg.id === globalPackageId);
  const globalProductsBySource = getGlobalProductsBySource(
    globalPackageDetail?.source,
    globalProducts,
    globalDependenciesDetails,
  );

  const updateProductIds = (affectedGridProducts ?? [])
    .map(async (product) => {
      const gridId = product.gridId;
      const curveId = product.id;
      const fromStatus = statusMap[curveId];
      if (toStatusIsGlobalProduct) {
        const hasGlobalProduct = product.product_global_dependencies.some((globalDep) => globalDep.global_product);

        if (hasGlobalProduct) {
          const id = globalProductsBySource.filter((p) =>
            product.product_global_dependencies.some((dep) => dep.global_product === p),
          );

          if (id.length) {
            toStatusMap[product.id] = id[0];
            return product.id;
          }
        }
      } else {
        const toStatusRules = knownStatusRules[toStatus as TKnownStatus];

        const canEod = !!product?.eod_product_dep;
        const canBroadcast = checkCanBroadcast(product?.packageByPackage.permissions);

        const resolvedRules = {
          isDeniedBroadcast,
          isLiteUser,
          canEod,
          canBroadcast,
        };

        const canTransitionToStatus = verifyKnownStatusRules(toStatusRules, resolvedRules);

        if (canTransitionToStatus) {
          logger.log("product:", { name: product.name });
          logger.log("to:", { toStatus });

          const requiredAction = transitionActions.find((action) => action.gridId === gridId);

          if (requiredAction) {
            const action = statusOperations[requiredAction.id];

            if (action === "write") {
              logger.log("write", {
                from: requiredAction.from,
                to: toStatus,
                gridId,
                curveId,
                product,
                fromStatus,
              });
              if (curveId && knownStatusList.includes(toStatus)) {
                await writeStatusInstruments({
                  to: toStatus,
                  curveId,
                });
              } else {
                console.error("gridId or curveId is missing");
              }
            }
          }

          toStatusMap[product.id] = toStatus;
          return product.id;
        }
      }
    })
    .filter(Boolean);

  const productIds = (await Promise.all(updateProductIds)).filter(Boolean);
  if (productIds?.length) {
    await updateColumnStatus(toStatusMap, productIds);
  }
}

function SelectStatusOnePage(props: TSelectStatusChangeAll) {
  const pageId = useOptionalActivePageId();
  const statusProducts = useStatusProducts(pageId);
  return <SelectStatusChangeAll {...props} statusProducts={statusProducts} />;
}

export function StatusModalMany({
  setClose,
  setCloseKeepMenu,
  open,
  anchorPosition,
  menuRef,
}: {
  setClose: () => unknown;
  setCloseKeepMenu: () => unknown;
  open: boolean;
  anchorPosition: { bottom: number; left: number };
  menuRef?: RefObject<HTMLDivElement>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const modalRef = useRef<HTMLDivElement>(null);
  const openedAtRef = useRef(Date.now());
  const ignoreOpeningTap = useCallback(() => Date.now() - openedAtRef.current < 500, []);

  const [toStatus, setToStatus] = useState<TStatus>("listen");
  const [affectedGridProducts, setAffectedGridProducts] = useState<TAffectedProducts>({});
  const [transitionActions, setTransitionActions] = useState<TStatusTransitionEnhanced[]>([]);

  const [statusOperations, setStatusOperations] = useState<Record<string, TUpdateStatusOperation>>({});

  const [pageScope, setPageScope] = useState<"current-page" | "all-pages">("current-page");

  const distinctTransitions = R.uniqueBy(transitionActions, (entry) => `${entry?.from}-${entry?.to}`);

  const isDeniedBroadcast = useUserHasRole("no-broadcast");
  const { liteUser } = useUserSubscriptionTier();

  const dependencies = affectedGridProducts[pageScope]?.flatMap((product) => product.product_global_dependencies) || [];
  const { data: globalProducts } = useGlobalProductsByProductIds(dependencies.map((dep) => dep.global_product));
  const { data: globalDependenciesDetails } = useGlobalPackagesByProductIds(
    dependencies.map((dep) => dep.global_product),
  );

  const updateColumnStatus = useUpdateColumnStatus();
  const writeStatusInstruments = useWriteStatusInstruments();
  const statusMap = useGridSettingsStatusMap();
  const isLiteUser = !!liteUser;
  async function onSave() {
    logger.log("-- ON SAVE --");
    logger.log("");

    setError(undefined);
    setIsLoading(true);

    try {
      await handleUpdateStatus({
        isDeniedBroadcast,
        isLiteUser,
        toStatus,
        affectedGridProducts: affectedGridProducts[pageScope],
        transitionActions,
        statusOperations,
        updateColumnStatus,
        writeStatusInstruments,
        statusMap,
        globalProducts: globalProducts ?? [],
        globalDependenciesDetails: globalDependenciesDetails ?? [],
      });

      setClose();
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setError("Something went wrong!");
    }
  }

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (ignoreOpeningTap()) return;

      const target = event.target as HTMLElement;

      if (modalRef.current?.contains(target)) {
        return;
      }

      // Check if click is on dropdown menu rendered via portal
      if (target.closest('[data-status-dropdown="true"]')) {
        return;
      }

      if (menuRef?.current?.contains(target)) {
        setCloseKeepMenu();
        return;
      }

      setClose();
    };

    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, setClose, setCloseKeepMenu, menuRef, ignoreOpeningTap]);

  const modalContent = (
    <div
      className="flex flex-col justify-center items-center p-[15px] gap-[12px] min-w-[205px]"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-row items-center justify-between gap-[9px] w-full">
        <span className="font-semibold text-lg text-foreground whitespace-nowrap">Change all sources</span>
        <CloseIcon onClick={setClose} />
      </div>
      <div className="flex items-center gap-[6px]">
        <span className="font-medium text-base text-foreground">Source:</span>
        <div className="w-[100px] h-[27px] min-w-[80px] max-w-[150px]">
          {pageScope === "current-page" ? (
            <SelectStatusOnePage
              toStatus={toStatus}
              pageScope={pageScope}
              setToStatus={setToStatus}
              affectedGridProducts={affectedGridProducts}
              setAffectedGridProducts={setAffectedGridProducts}
              transitionActions={transitionActions}
              setTransitionActions={setTransitionActions}
            />
          ) : (
            <SelectStatusAllPages
              toStatus={toStatus}
              pageScope={pageScope}
              setToStatus={setToStatus}
              affectedGridProducts={affectedGridProducts}
              setAffectedGridProducts={setAffectedGridProducts}
              transitionActions={transitionActions}
              setTransitionActions={setTransitionActions}
            />
          )}
        </div>
      </div>
      <RadioGroup>
        <Radio
          name="pageScope"
          value="all-pages"
          label="All Pages"
          checked={pageScope === "all-pages"}
          onChange={(event) => {
            setPageScope(event.target.value as "current-page" | "all-pages");
            setTransitionActions([]);
            setError(undefined);
          }}
        />
        <Radio
          name="pageScope"
          value="current-page"
          label="This Page"
          checked={pageScope === "current-page"}
          onChange={(event) => {
            setPageScope(event.target.value as "current-page" | "all-pages");
            setTransitionActions([]);
            setError(undefined);
          }}
        />
      </RadioGroup>
      {pageScope === "all-pages" && (
        <p className="text-base text-foreground-muted flex-[1_0_0] w-full">
          NB: Each Curve can only be in one status across all of your Pages
        </p>
      )}
      {distinctTransitions?.length > 0 ? (
        <div className="pt-[12px]">
          <div className="flex flex-col gap-[12px]">
            {distinctTransitions.map((action) => {
              const productNamesPerAction = transitionActions
                .filter((entry) => entry.id === action.id)
                .map((entry) => entry.productName)
                .join(", ");

              return (
                <div key={action.id}>
                  <span className="capitalize font-bold text-base text-foreground mb-2 flex items-center gap-1">
                    {action.label}
                    <LuInfo title={productNamesPerAction} />
                  </span>
                  <Dropdown
                    value={statusOperations[action.id] ? statusOperations[action.id] : "read"}
                    onChange={(value) => {
                      setStatusOperations((prev) => ({
                        ...prev,
                        [action.id]: StatusOperationSchema.parse(value),
                      }));
                    }}
                    options={action.options.map((option) => ({
                      value: option.value,
                      label: option.label,
                    }))}
                    className="capitalize"
                  />
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        ""
      )}
      <div className="flex gap-4 flex-row justify-end">
        <Button buttonType="cancel" onClick={setClose}>
          Cancel
        </Button>
        <Button buttonType="save" disabled={isLoading} onClick={onSave}>
          {isLoading ? "Saving..." : "Save"}
        </Button>
      </div>
      {error && <p className="pt-[12px] text-base text-danger">{error}</p>}
    </div>
  );

  const modalStyle = {
    maxWidth: statusModalMaxWidth,
    zIndex: changeAllZIndex,
  };

  if (anchorPosition) {
    return (
      <div
        className="fixed inset-0 z-[9999]"
        onClick={() => {
          if (ignoreOpeningTap()) return;
          setClose();
        }}
      >
        <div
          ref={modalRef}
          className={cn("fixed overflow-hidden", cardContainer)}
          style={{
            ...modalStyle,
            bottom: anchorPosition.bottom,
            left: anchorPosition.left,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: `calc(100dvh - ${anchorPosition.bottom}px - 16px)`,
            overflowY: "auto",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {modalContent}
        </div>
      </div>
    );
  }

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex justify-center items-center bg-black/50 dark:bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className={cn("overflow-hidden", cardContainer)}
        style={modalStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {modalContent}
      </div>
    </div>,
    document.body,
  );
}

type TSelectStatusChangeAll = {
  toStatus: TStatus;
  pageScope: "current-page" | "all-pages";
  setToStatus: React.Dispatch<React.SetStateAction<TStatus>>;
  transitionActions: TStatusTransitionEnhanced[];
  setTransitionActions: React.Dispatch<React.SetStateAction<TStatusTransitionEnhanced[]>>;
  affectedGridProducts: TAffectedProducts;
  setAffectedGridProducts: React.Dispatch<React.SetStateAction<TAffectedProducts>>;
};

function SelectStatusChangeAll({
  statusProducts,
  toStatus,
  pageScope,
  setToStatus,
  setTransitionActions,
  affectedGridProducts,
  setAffectedGridProducts,
}: TSelectStatusChangeAll & {
  statusProducts: TProductWithGridIdAndStatus[];
}) {
  const settings = useGridSettings(useUserId());
  useEffect(() => {
    if (!affectedGridProducts[pageScope] && statusProducts.length) {
      logger.log("set affectedGridProducts in chg all modal");
      setAffectedGridProducts((prev) => ({
        ...prev,
        [pageScope]: statusProducts,
      }));
    }
  }, [pageScope, statusProducts, setAffectedGridProducts, affectedGridProducts]);

  const canAtLeastOneEod = statusProducts?.find((product) => product?.eod_product_dep);
  const canAtLeastOneBroadcast = statusProducts?.find((product) =>
    checkCanBroadcast(product?.packageByPackage.permissions),
  );
  const { liteUser } = useUserSubscriptionTier();
  const resolvedRules = {
    isDeniedBroadcast: useUserHasRole("no-broadcast"),
    isLiteUser: !!liteUser,
    canEod: !!canAtLeastOneEod,
    canBroadcast: !!canAtLeastOneBroadcast,
  };

  const logo = statusProducts?.[0]?.packageByPackage?.sourceBySource?.logo ?? undefined;

  const globalOptions = useGlobalDependencyOptions(statusProducts ?? []);

  const statusOptionsToRender = genSelectStatusOptions({
    resolvedRules,
    globalOptions,
    settings: settings.data,
  });

  const allStatusOptions = genSelectStatusOptions({
    resolvedRules,
    globalOptions,
    settings: settings.data,
  });

  const statusMap = useGridSettingsStatusMap();
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const handleStatusChange = (value: TStatus) => {
    const transitionActions = R.pipe(
      statusProducts || [],
      R.map((product) => {
        const transitionAction = genStatusTransitionActions({
          from: statusMap?.[product.id] || "listen",
          to: value,
        });

        return (
          transitionAction && {
            ...transitionAction,
            productId: product.id,
            gridId: product.gridId,
            productName: productName(product),
          }
        );
      }),
    ).filter(Boolean);

    setTransitionActions(transitionActions);
    setToStatus(value);
    setIsOpen(false);
  };

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest('[data-status-dropdown="true"]')) {
          return;
        }
        if (isOpen) setIsOpen(false);
      }}
    >
      <div ref={selectRef} className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between w-full h-[27px] px-2 border border-outline rounded-md bg-surface-1 text-foreground text-sm cursor-pointer hover:bg-surface-2"
        >
          <SelectPrettyValue
            options={statusOptionsToRender}
            renderValueParams={{ value: toStatus, label: "" }}
            logo={logo}
          />
          <FaChevronDown className="text-foreground-muted" style={{ marginTop: 2, marginRight: 2 }} />
        </button>
        {isOpen &&
          createPortal(
            <div
              data-status-dropdown="true"
              className="fixed bg-surface-1 border border-outline rounded-md shadow-lg z-[9999] max-h-60 overflow-y-auto"
              style={{
                top: selectRef.current ? selectRef.current.getBoundingClientRect().bottom + 4 : 0,
                left: selectRef.current ? selectRef.current.getBoundingClientRect().left : 0,
                width: selectRef.current ? selectRef.current.getBoundingClientRect().width : "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {allStatusOptions
                .filter((opt) => statusOptionsToRender.some((r) => r.value === opt.value))
                .map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    data-value={option.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusChange(option.value);
                    }}
                    className={cn(
                      "flex items-center justify-center w-full px-2 py-1.5 text-sm cursor-pointer hover:bg-[var(--neutral-tonal)] text-foreground",
                      option.value === toStatus && "bg-[var(--neutral-tonal)]",
                    )}
                    style={option.style}
                  >
                    <SelectPrettyValue
                      options={allStatusOptions}
                      renderValueParams={{ value: option.value, label: option.label }}
                      logo={logo}
                    />
                  </button>
                ))}
            </div>,
            document.body,
          )}
      </div>
    </ClickAwayListener>
  );
}

function SelectStatusAllPages(props: TSelectStatusChangeAll) {
  const statusProducts = useStatusProducts();
  return <SelectStatusChangeAll {...props} statusProducts={statusProducts} />;
}

const useStatusProducts = (pageId?: string) => {
  const pageProducts = useAllPageProducts(useUserId());
  const selectedProducts = pageId ? pageProducts.data?.filter((p) => p.page_id === pageId) : pageProducts.data;
  const ids = selectedProducts?.map((p) => p.product_id).filter(Boolean) || [];
  const { data: products } = useProductsByIds(ids);
  const statusMap = useGridSettingsStatusMap();
  return useMemo(
    () => joinProductsWithGridPageProducts(products ?? [], selectedProducts ?? [], statusMap),
    [selectedProducts, products, statusMap],
  );
};
